import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import AdminPage from "@/pages/AdminPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  ArrowRight,
  Users,
  Leaf,
  Handshake,
  ShieldCheck,
  Tractor,
  Sprout,
  Truck,
  ClipboardCheck,
  MapPin,
  Menu,
  X,
  Facebook,
  Instagram,
  Send,
} from "lucide-react";
import {
  translations,
  owners,
  LOGO_URL,
  HERO_BG,
  ABOUT_IMG,
  GALLERY_IMGS,
} from "@/i18n/translations";
import {
  WhyChooseUs,
  WhatsAppButton,
  FreeConsultation,
} from "@/components/Sections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURE_ICONS = { users: Users, leaf: Leaf, handshake: Handshake, shield: ShieldCheck };
const SERVICE_ICONS = {
  farmer: Tractor,
  leaf: Leaf,
  sprout: Sprout,
  truck: Truck,
  clipboard: ClipboardCheck,
};

/* ----------------- Top utility bar ----------------- */
const TopBar = ({ lang, setLang, t }) => (
  <div className="hidden md:block bg-black border-b border-line text-[12px] text-white/70" data-testid="top-bar">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 h-10 flex items-center justify-between">
      <div className="flex items-center gap-2" data-testid="language-toggle">
        <button
          onClick={() => setLang("en")}
          className={`uppercase tracking-wider transition ${lang === "en" ? "text-lime-400 font-semibold" : "hover:text-white"}`}
          data-testid="lang-en"
        >
          {t.topbar.english}
        </button>
        <span className="text-white/30">|</span>
        <button
          onClick={() => setLang("es")}
          className={`uppercase tracking-wider transition ${lang === "es" ? "text-lime-400 font-semibold" : "hover:text-white"}`}
          data-testid="lang-es"
        >
          {t.topbar.spanish}
        </button>
      </div>
      <div className="flex items-center gap-7">
        <a href="mailto:norberto.monroy@agnmusa.com" className="flex items-center gap-2 hover:text-lime-400 transition" data-testid="topbar-email">
          <Mail className="h-3.5 w-3.5" />
          norberto.monroy@agnmusa.com
        </a>
        <a href="tel:+12694643114" className="flex items-center gap-2 hover:text-lime-400 transition" data-testid="topbar-phone">
          <Phone className="h-3.5 w-3.5" />
          269-464-3114
        </a>
      </div>
    </div>
  </div>
);

/* ----------------- Main nav ----------------- */
const Nav = ({ t }) => {
  const [open, setOpen] = useState(false);
  const items = [
    { id: "home", label: t.nav.home },
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "gallery", label: t.nav.gallery },
    { id: "contact", label: t.nav.contact },
  ];
  return (
    <header className="sticky top-0 z-50 bg-black/95 backdrop-blur border-b border-line" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3" data-testid="brand-link">
          <img src={LOGO_URL} alt="AGNM Contracting LLC" className="h-12 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-8 text-[13px] uppercase tracking-[0.15em] text-white/80 font-medium">
          {items.map((it, i) => (
            <a
              key={i}
              href={`#${it.id}`}
              className="hover:text-lime-400 transition relative py-2"
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {it.label}
            </a>
          ))}
        </nav>

        <a href="#worker-application" className="hidden lg:block">
          <Button
            className="bg-gradient-to-b from-zinc-200 to-zinc-400 hover:from-lime-400 hover:to-lime-500 text-black font-bold uppercase tracking-[0.15em] px-7 h-11 rounded-md transition"
            data-testid="nav-apply-now"
          >
            {t.nav.applyNow}
          </Button>
        </a>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-line bg-black" data-testid="mobile-menu">
          <div className="px-6 py-5 flex flex-col gap-4 text-sm uppercase tracking-[0.15em] text-white/80">
            {items.map((it, i) => (
              <a key={i} href={`#${it.id}`} onClick={() => setOpen(false)} className="hover:text-lime-400">
                {it.label}
              </a>
            ))}
            <a href="#contact" onClick={() => setOpen(false)} className="text-lime-400 font-bold">
              {t.nav.applyNow}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ----------------- Hero ----------------- */
const Hero = ({ t }) => (
  <section id="home" className="relative bg-black overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0">
      <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 hero-overlay" />
      {/* Extra dark overlay for mobile readability */}
      <div className="absolute inset-0 bg-black/50 md:bg-transparent" />
    </div>
    <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 pt-14 pb-20 sm:pt-20 sm:pb-24 lg:pt-28 lg:pb-36 min-h-[540px] sm:min-h-[600px] lg:min-h-[640px] flex items-center">
      <div className="max-w-2xl animate-fade-up w-full">
        <div className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-white font-bold leading-[0.95] tracking-tight">
          AGNM
        </div>
        <div className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white/95 tracking-[0.2em] sm:tracking-[0.25em] mt-2">
          CONTRACTING LLC
        </div>
        <div className="h-px w-24 sm:w-32 bg-white/70 my-5 sm:my-7" />
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-tight uppercase">
          {t.hero.titleA}
          <br />
          <span className="text-lime-400">{t.hero.titleB}</span>
        </h1>
        <p className="mt-5 sm:mt-6 text-sm sm:text-base text-white/80 max-w-lg leading-relaxed">
          {t.hero.subtitle}
        </p>
        <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
          <a href="#contact" className="w-full sm:w-auto">
            <Button
              className="w-full sm:w-auto bg-lime-500 hover:bg-lime-400 text-black font-bold uppercase tracking-[0.15em] px-7 h-12 rounded-md"
              data-testid="hero-cta-apply"
            >
              <Users className="mr-2 h-4 w-4" />
              {t.hero.ctaPrimary}
            </Button>
          </a>
          <a href="#contact" className="w-full sm:w-auto">
            <Button
              variant="outline"
              className="w-full sm:w-auto border-white/40 text-white hover:bg-white hover:text-black font-bold uppercase tracking-[0.15em] px-7 h-12 rounded-md bg-transparent"
              data-testid="hero-cta-contact"
            >
              <Phone className="mr-2 h-4 w-4" />
              {t.hero.ctaSecondary}
            </Button>
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* ----------------- Feature strip ----------------- */
const FeatureStrip = ({ t }) => (
  <section className="bg-black border-y border-line" data-testid="features-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 py-10 sm:py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7 sm:gap-8 lg:gap-10">
      {t.features.map((f, i) => {
        const Icon = FEATURE_ICONS[f.icon] || Users;
        return (
          <div key={i} className="flex items-start gap-4" data-testid={`feature-${i}`}>
            <div className="flex-shrink-0">
              <Icon className="h-9 w-9 sm:h-10 sm:w-10 text-lime-400" strokeWidth={1.6} />
            </div>
            <div>
              <div className="font-display text-sm sm:text-base uppercase tracking-[0.12em] text-white mb-2">
                {f.title}
              </div>
              <p className="text-sm text-white/65 leading-relaxed">{f.body}</p>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

/* ----------------- About ----------------- */
const About = ({ t }) => (
  <section id="about" className="bg-zinc-100 text-black py-16 sm:py-20 lg:py-24" data-testid="about-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 sm:gap-12 items-center">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-lime-600 font-bold mb-3 flex items-center gap-3">
          {t.about.kicker}
          <span className="h-px w-12 bg-lime-500" />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-black uppercase font-bold leading-tight tracking-tight">
          {t.about.title}
        </h2>
        <p className="mt-5 sm:mt-6 text-sm sm:text-base text-zinc-700 leading-relaxed">{t.about.body}</p>
        <a href="#services" className="inline-block w-full sm:w-auto">
          <Button
            className="mt-7 w-full sm:w-auto bg-black hover:bg-zinc-800 text-white font-bold uppercase tracking-[0.15em] px-7 h-11 rounded-md"
            data-testid="about-cta"
          >
            {t.about.cta}
          </Button>
        </a>
      </div>
      <div className="relative aspect-[16/10] rounded-md overflow-hidden border border-zinc-300 shadow-lg">
        <img src={ABOUT_IMG} alt="Agricultural fields" className="w-full h-full object-cover" />
      </div>
    </div>
  </section>
);

/* ----------------- Services ----------------- */
const Services = ({ t }) => (
  <section id="services" className="bg-black py-16 sm:py-20 lg:py-24" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10">
      <div className="text-center mb-10 sm:mb-14">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase font-bold tracking-tight">
          {t.services.kicker}
        </h2>
        <div className="h-0.5 w-20 bg-lime-500 mx-auto mt-4" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
        {t.services.items.map((s, i) => {
          const Icon = SERVICE_ICONS[s.icon] || Sprout;
          return (
            <div
              key={i}
              className="bg-surface border border-line rounded-md p-6 sm:p-7 text-center hover:border-lime-500 hover:-translate-y-1 transition-all duration-300"
              data-testid={`service-card-${i}`}
            >
              <Icon className="h-10 w-10 sm:h-12 sm:w-12 text-lime-400 mx-auto mb-4 sm:mb-5" strokeWidth={1.6} />
              <h3 className="font-display text-base uppercase tracking-[0.1em] text-white font-bold mb-3 leading-tight sm:min-h-[2.5rem]">
                {s.title}
              </h3>
              <p className="text-sm text-white/65 leading-relaxed">{s.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ----------------- Gallery ----------------- */
const Gallery = ({ t }) => (
  <section id="gallery" className="bg-zinc-100 py-16 sm:py-20 lg:py-24" data-testid="gallery-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10">
      <div className="text-center mb-10 sm:mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-lime-600 font-bold mb-3">
          {t.gallery.kicker}
        </div>
        <h2 className="font-display text-3xl sm:text-4xl text-black uppercase font-bold tracking-tight">
          {t.gallery.title}
        </h2>
        <div className="h-0.5 w-20 bg-lime-500 mx-auto mt-4" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
        {GALLERY_IMGS.map((src, i) => (
          <div
            key={i}
            className="aspect-square overflow-hidden rounded-md border border-zinc-300 group"
            data-testid={`gallery-image-${i}`}
          >
            <img
              src={src}
              alt={`Gallery ${i + 1}`}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ----------------- Contact ----------------- */
const Contact = ({ t, lang }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    audience: "worker",
    visa_type: "H2A",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (key) => (e) =>
    setForm({ ...form, [key]: e.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        audience: "worker",
        visa_type: "H2A",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-black py-16 sm:py-20 lg:py-24" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* Left: Heading */}
          <div className="sm:col-span-2 lg:col-span-3" data-testid="contact-heading">
            <h2 className="font-display text-3xl sm:text-4xl text-white uppercase font-bold tracking-tight">
              {t.contact.kicker}
            </h2>
            <div className="mt-4 text-xs uppercase tracking-[0.2em] text-lime-400 font-bold">
              {t.contact.subKicker}
            </div>
            <div className="h-0.5 w-12 bg-lime-500 mt-3" />
            <p className="mt-4 sm:mt-5 text-sm text-white/65 leading-relaxed">{t.contact.body}</p>
          </div>

          {/* Owner cards */}
          {owners.map((o, i) => (
            <div
              key={i}
              className="lg:col-span-3 bg-zinc-100 text-black rounded-md p-5 sm:p-6"
              data-testid={`owner-card-${i}`}
            >
              <div className="font-display text-lg uppercase tracking-[0.1em] font-bold">{o.name}</div>
              <div className="h-0.5 w-8 bg-lime-500 my-3" />
              <a
                href={`tel:${o.phone.replace(/[^0-9+]/g, "")}`}
                className="flex items-center gap-2 text-zinc-800 hover:text-lime-600 transition mt-3 font-medium"
                data-testid={`owner-${i}-phone`}
              >
                <Phone className="h-4 w-4 text-lime-600 flex-shrink-0" />
                {o.phone}
              </a>
              <a
                href={`mailto:${o.email}`}
                className="flex items-center gap-2 text-zinc-700 hover:text-lime-600 transition mt-3 text-sm break-all"
                data-testid={`owner-${i}-email`}
              >
                <Mail className="h-4 w-4 text-lime-600 flex-shrink-0" />
                {o.email}
              </a>
            </div>
          ))}

          {/* Service Area */}
          <div className="sm:col-span-2 lg:col-span-3 bg-surface border border-line rounded-md p-5 sm:p-6 relative overflow-hidden" data-testid="service-area-card">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-5 w-5 text-lime-400" />
              <div className="font-display text-base uppercase tracking-[0.1em] text-white font-bold">
                {t.contact.serviceArea}
              </div>
            </div>
            <p className="text-sm text-white/65 leading-relaxed">{t.contact.serviceAreaBody}</p>
            <svg
              className="mt-4 w-full text-white/15"
              viewBox="0 0 200 200"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              {/* Michigan Lower Peninsula (mitten) */}
              <path d="M55,60 L72,52 L88,48 L102,46 L118,48 L130,54 L138,64 L142,78 L140,94 L136,108 L130,122 L122,134 L112,144 L100,152 L88,156 L78,154 L70,148 L66,138 L64,124 L58,112 L52,98 L50,84 L52,72 Z" />
              {/* Upper Peninsula */}
              <path d="M30,38 L60,30 L90,28 L120,32 L150,30 L168,36 L172,46 L160,52 L140,50 L120,52 L100,50 L80,52 L60,50 L42,52 L32,48 Z" />
              {/* Marker on lower mitten center */}
              <circle cx="96" cy="100" r="4" fill="#a3e635" />
            </svg>
          </div>
        </div>

        {/* Contact Form */}
        <div className="mt-8 sm:mt-10 bg-surface border border-line rounded-md p-5 sm:p-7 lg:p-10">
          <form onSubmit={onSubmit} data-testid="contact-form">
            <div className="font-display text-xl uppercase tracking-[0.15em] text-white font-bold mb-6">
              {t.contact.formTitle}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name" className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                  {t.contact.fields.name}
                </Label>
                <Input id="c-name" required value={form.name} onChange={onChange("name")} className="mt-1.5 bg-black border-line text-white" data-testid="contact-input-name" />
              </div>
              <div>
                <Label htmlFor="c-email" className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                  {t.contact.fields.email}
                </Label>
                <Input id="c-email" type="email" required value={form.email} onChange={onChange("email")} className="mt-1.5 bg-black border-line text-white" data-testid="contact-input-email" />
              </div>
              <div>
                <Label htmlFor="c-phone" className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                  {t.contact.fields.phone}
                </Label>
                <Input id="c-phone" value={form.phone} onChange={onChange("phone")} className="mt-1.5 bg-black border-line text-white" data-testid="contact-input-phone" />
              </div>
              <div>
                <Label htmlFor="c-company" className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                  {t.contact.fields.company}
                </Label>
                <Input id="c-company" value={form.company} onChange={onChange("company")} className="mt-1.5 bg-black border-line text-white" data-testid="contact-input-company" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] text-white/60">{t.contact.fields.audience}</Label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger className="mt-1.5 bg-black border-line text-white" data-testid="contact-select-audience">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employer">{t.contact.audienceOptions.employer}</SelectItem>
                    <SelectItem value="worker">{t.contact.audienceOptions.worker}</SelectItem>
                    <SelectItem value="general">{t.contact.audienceOptions.general}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] text-white/60">{t.contact.fields.visa}</Label>
                <Select value={form.visa_type} onValueChange={(v) => setForm({ ...form, visa_type: v })}>
                  <SelectTrigger className="mt-1.5 bg-black border-line text-white" data-testid="contact-select-visa">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="H2A">{t.contact.visaOptions.H2A}</SelectItem>
                    <SelectItem value="unsure">{t.contact.visaOptions.unsure}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label htmlFor="c-message" className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                {t.contact.fields.message}
              </Label>
              <Textarea
                id="c-message"
                required
                rows={5}
                value={form.message}
                onChange={onChange("message")}
                className="mt-1.5 bg-black border-line text-white"
                placeholder={lang === "es" ? "Cuéntenos sobre su solicitud..." : "Tell us about your needs..."}
                data-testid="contact-input-message"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="mt-6 bg-lime-500 hover:bg-lime-400 text-black font-bold uppercase tracking-[0.15em] px-7 h-12 rounded-md"
              data-testid="contact-submit-btn"
            >
              {submitting ? t.contact.submitting : t.contact.submit}
              {!submitting && <Send className="ml-2 h-4 w-4" />}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ----------------- Footer ----------------- */
const Footer = ({ t }) => (
  <footer className="bg-black border-t border-line pt-10 pb-6" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
      <div className="flex items-center justify-center md:justify-start gap-3">
        <img src={LOGO_URL} alt="AGNM" className="h-12 w-auto" />
      </div>
      <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] sm:text-[12px] uppercase tracking-[0.15em] text-white/70">
        <a href="#home" className="hover:text-lime-400">{t.nav.home}</a>
        <a href="#about" className="hover:text-lime-400">{t.nav.about}</a>
        <a href="#services" className="hover:text-lime-400">{t.nav.services}</a>
        <a href="#gallery" className="hover:text-lime-400">{t.nav.gallery}</a>
        <a href="#contact" className="hover:text-lime-400">{t.nav.contact}</a>
      </nav>
      <div className="flex justify-center md:justify-end gap-3">
        <a href="#" className="h-9 w-9 rounded-full border border-line flex items-center justify-center text-white/70 hover:bg-lime-500 hover:text-black hover:border-lime-500 transition" aria-label="Facebook" data-testid="social-facebook">
          <Facebook className="h-4 w-4" />
        </a>
        <a href="#" className="h-9 w-9 rounded-full border border-line flex items-center justify-center text-white/70 hover:bg-lime-500 hover:text-black hover:border-lime-500 transition" aria-label="Instagram" data-testid="social-instagram">
          <Instagram className="h-4 w-4" />
        </a>
        <a href="mailto:norberto.monroy@agnmusa.com" className="h-9 w-9 rounded-full border border-line flex items-center justify-center text-white/70 hover:bg-lime-500 hover:text-black hover:border-lime-500 transition" aria-label="Email" data-testid="social-email">
          <Mail className="h-4 w-4" />
        </a>
      </div>
    </div>
    <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 mt-8 pt-5 border-t border-line text-center text-[11px] sm:text-[12px] text-white/50">
      © {new Date().getFullYear()} {t.footer.copyright}
    </div>
  </footer>
);

/* ----------------- Landing Page ----------------- */
const LandingPage = () => {
  const [lang, setLang] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("agnm_lang") || "en";
    }
    return "en";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("agnm_lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  const t = translations[lang];

  return (
    <div className="min-h-screen bg-black text-white font-sans" data-testid="landing-page">
      <TopBar lang={lang} setLang={setLang} t={t} />
      <Nav t={t} />
      <main>
        <Hero t={t} />
        <FeatureStrip t={t} />
        <FreeConsultation lang={lang} />
        <Services t={t} />
        <WhyChooseUs lang={lang} t={t} />
        <About t={t} />
        <Gallery t={t} />
        <Contact t={t} lang={lang} />
      </main>
      <Footer t={t} />
      <WhatsAppButton lang={lang} />
      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
